import * as Api from '@ViewModels';
import * as React from 'react';
import { getDisplayName } from '../../models/NameUtils';
import { useAccountUserCount } from '../../queries/Account/useAccountUserCount';
import { useLoadAllUsers } from '../../queries/User/useLoadAllUsers';

interface UseUserDropdownProps {
	accountId: string;
	currentUserId: string;
	initialSelectedUser?: Api.IUser;
}

interface UseUserDropdownReturn {
	// State
	selectedUser: Api.IUser;
	showSelectEmployeeModal: boolean;

	// User data
	users: Api.IUser[];
	isLoading: boolean;
	totalUserCount: number;
	shouldShowUsersInDropdown: boolean;
	activeEmployees: Api.IUser[];

	// Actions
	setSelectedUser: (user: Api.IUser) => void;
	setShowSelectEmployeeModal: (show: boolean) => void;
	handleCurrentUserSelect: () => void;
	handleSearchEmployeeClick: () => void;
	handleEmployeeSelect: (user: Api.IUser) => void;
	handleEmployeeSelectedFromModal: (user: Api.IUser) => void;
}

/**
 * Shared hook for user dropdown logic used in both UserSelectionModal and AdminMeetingLinksDropdown
 * Encapsulates user loading, filtering, selection, and modal management
 */
export function useUserDropdown({
	accountId,
	currentUserId,
	initialSelectedUser,
}: UseUserDropdownProps): UseUserDropdownReturn {
	const [selectedUser, setSelectedUser] = React.useState<Api.IUser>(() => {
		// Find current user from initialSelectedUser or create a placeholder
		return initialSelectedUser || ({ id: currentUserId } as Api.IUser);
	});
	const [showSelectEmployeeModal, setShowSelectEmployeeModal] = React.useState(false);

	// Get total user count to determine if we should load all users in dropdown
	const userCountQuery = useAccountUserCount({
		accountId,
	});

	// Load first page of users (500) to potentially show in dropdown
	const { users, isLoading } = useLoadAllUsers({
		pageSize: 500,
		meta: {
			toastErrorMessage: 'Failed to load users for dropdown',
		},
	});

	const totalUserCount = userCountQuery.data || 0;
	const shouldShowUsersInDropdown = totalUserCount <= 500 && users.length > 0;

	// Filter to only active users, sort alphabetically, exclude current user
	const activeEmployees = React.useMemo(() => {
		if (!shouldShowUsersInDropdown || !users.length) return [];

		return users
			.filter((user: Api.IUser) => user.activationStatus === 'ACTIVE' && user.id !== currentUserId)
			.sort((a: Api.IUser, b: Api.IUser) => getDisplayName(a).localeCompare(getDisplayName(b)));
	}, [users, currentUserId, shouldShowUsersInDropdown]);

	// Update selected user if initialSelectedUser changes
	React.useEffect(() => {
		if (initialSelectedUser) {
			setSelectedUser(initialSelectedUser);
		}
	}, [initialSelectedUser]);

	// Handler functions
	const handleCurrentUserSelect = React.useCallback(() => {
		const currentUser = users.find((u: Api.IUser) => u.id === currentUserId) || ({ id: currentUserId } as Api.IUser);
		setSelectedUser(currentUser);
	}, [users, currentUserId]);

	const handleSearchEmployeeClick = React.useCallback(() => {
		setShowSelectEmployeeModal(true);
	}, []);

	const handleEmployeeSelect = React.useCallback((user: Api.IUser) => {
		setSelectedUser(user);
	}, []);

	const handleEmployeeSelectedFromModal = React.useCallback((user: Api.IUser) => {
		setSelectedUser(user);
		setShowSelectEmployeeModal(false);
	}, []);

	return {
		// State
		selectedUser,
		showSelectEmployeeModal,

		// User data
		users,
		isLoading,
		totalUserCount,
		shouldShowUsersInDropdown,
		activeEmployees,

		// Actions
		setSelectedUser,
		setShowSelectEmployeeModal,
		handleCurrentUserSelect,
		handleSearchEmployeeClick,
		handleEmployeeSelect,
		handleEmployeeSelectedFromModal,
	};
}
